import { useEffect, useRef, useState } from "react";
import { useSearchParams, Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/lib/api";
import { useEntitlement } from "@/lib/entitlement";
import { trackPurchase } from "@/lib/tiktok";
import { trackGAPurchase } from "@/lib/ga";
import { logWarn } from "@/lib/log";
import { CheckCircle2, XCircle } from "lucide-react";

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const { refresh } = useEntitlement();
  const navigate = useNavigate();
  const handledRef = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    let attempts = 0;
    const poll = async () => {
      try {
        const r = await axios.get(`${API}/payments/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          // Idempotency guard: StrictMode / remounts double-invoke this effect,
          // which would otherwise fire two redirect timers and clobber the target.
          if (handledRef.current) return;
          handledRef.current = true;
          setStatus("paid");
          trackPurchase();
          trackGAPurchase();
          await refresh();
          // Resolve the destination up-front (before clearing any flag) so this
          // runs exactly once and lands on the right page.
          const fromSpeedDating = !!sessionStorage.getItem("sd_pending");
          sessionStorage.removeItem("sd_pending");
          const hasResults = !!sessionStorage.getItem("pm_results");
          const dest = fromSpeedDating ? "/speed-dating" : (hasResults ? "/results" : "/saved");
          setTimeout(() => navigate(dest, { replace: true }), 1600);
          return;
        }
      } catch (error) {
        // Transient poll failures are expected while Stripe finalises the
        // session; we retry below and surface a timeout state if it never lands.
        logWarn("Payment status poll failed (will retry):", error?.message);
      }
      attempts++;
      if (attempts < 12) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId, refresh, navigate]);

  // Explicit three-state render (paid / timeout / checking) — no nested ternaries.
  let content;
  if (status === "paid") {
    content = (
      <>
        <div className="w-16 h-16 rounded-full bg-primary/15 text-primary grid place-items-center mx-auto">
          <CheckCircle2 size={32} />
        </div>
        <h1 className="mt-6 font-display font-bold text-4xl text-accent">You're unlocked 🎉</h1>
        <p className="mt-3 text-muted-foreground">Every match, cost breakdown and save — yours for life. Taking you to your results…</p>
        <Link to="/results" data-testid="success-continue"
              className="pill-btn mt-8 inline-flex items-center rounded-full bg-primary text-primary-foreground px-7 py-3 font-semibold">
          See all my matches now
        </Link>
      </>
    );
  } else if (status === "timeout") {
    content = (
      <>
        <XCircle size={40} className="mx-auto text-destructive" />
        <p className="mt-4 text-muted-foreground">Payment is still processing. Refresh in a minute — if it doesn't update, contact support.</p>
      </>
    );
  } else {
    content = <p className="text-muted-foreground animate-pulse">Confirming your payment…</p>;
  }

  return (
    <div className="max-w-xl mx-auto px-6 pt-24 pb-24 text-center">
      {content}
    </div>
  );
}

export function PaymentCancel() {
  return (
    <div className="max-w-xl mx-auto px-6 pt-24 pb-24 text-center">
      <h1 className="font-display font-bold text-4xl text-accent">No worries — nothing was charged.</h1>
      <p className="mt-4 text-muted-foreground">Come back anytime.</p>
      <Link to="/upgrade" data-testid="cancel-back"
            className="pill-btn mt-8 inline-flex items-center rounded-full bg-primary text-primary-foreground px-7 py-3 font-semibold">
        Back to Unlock
      </Link>
    </div>
  );
}
